/* ==========================================================================
   CONTREPIED — ajout au panier sans rechargement + animation « balle de padel »
   Reprend le contrat d'Horizon : CartLinesUpdateEvent (compteur, tiroir re-rendu).
   Animation (mix) : une raquette de padel frappe la balle, qui part d'abord à
   l'opposé (le contre-pied), revient en courbe taper la vitre au bord de l'écran,
   rebondit et retombe dans le panier. « +1 » lime à l'arrivée.
   Désactivée si l'utilisateur préfère réduire les animations.
   Sans JavaScript, le formulaire reste un envoi classique vers /cart/add.
   ========================================================================== */
import { CartLinesUpdateEvent } from '@shopify/events';

const root = window.Shopify?.routes?.root || '/';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const openDrawer = () => {
  const drawer = document.getElementById('cart-drawer');
  if (drawer && typeof drawer.open === 'function') { drawer.open(); return true; }
  return false;
};

const updateLabels = (count) => {
  for (const el of document.querySelectorAll('[data-cp-cart-label]')) {
    el.textContent = count > 0 ? `Panier, ${count} article${count > 1 ? 's' : ''}` : 'Panier vide';
  }
};

/* --- Visuels ------------------------------------------------------------- */
let uid = 0;
const ballSVG = () => {
  const id = 'cpball' + ++uid;
  return `<svg viewBox="0 0 40 40" width="100%" height="100%"><defs><radialGradient id="${id}f" cx="35%" cy="30%" r="75%"><stop offset="0" stop-color="#F6FF7A"/><stop offset=".55" stop-color="#DCF21C"/><stop offset="1" stop-color="#9DB00A"/></radialGradient><clipPath id="${id}c"><circle cx="20" cy="20" r="19"/></clipPath></defs><circle cx="20" cy="20" r="19" fill="url(#${id}f)"/><g clip-path="url(#${id}c)" fill="none" stroke-linecap="round"><path d="M9 1C21 10 21 30 9 39M31 1C19 10 19 30 31 39" stroke="#7E8C08" stroke-width="4.2" opacity=".35"/><path d="M9 1C21 10 21 30 9 39M31 1C19 10 19 30 31 39" stroke="#fff" stroke-width="2.6"/></g><ellipse cx="13" cy="11" rx="6" ry="3.5" fill="#fff" opacity=".35" transform="rotate(-30 13 11)"/></svg>`;
};

/* Raquette de padel : tête pleine perforée, cadre épais, cœur ouvert (triangle), manche court avec grip et dragonne */
const racketSVG = () => {
  const id = 'cprk' + ++uid;
  return `<svg viewBox="0 0 80 150" width="100%" height="100%">
<defs>
<pattern id="${id}h" width="7" height="7" patternUnits="userSpaceOnUse"><circle cx="3.5" cy="3.5" r="1.6" fill="#F7F6F1" opacity=".55"/></pattern>
<clipPath id="${id}f"><path d="M40 8C60 8 71 22 71 42C71 60 62 72 50 78H30C18 72 9 60 9 42C9 22 20 8 40 8Z"/></clipPath>
</defs>
<path d="M40 3C64 3 76 19 76 42C76 63 65 77 52 84L46 100H34L28 84C15 77 4 63 4 42C4 19 16 3 40 3Z" fill="#041650"/>
<path d="M40 8C60 8 71 22 71 42C71 60 62 72 50 78H30C18 72 9 60 9 42C9 22 20 8 40 8Z" fill="#12245E"/>
<rect x="0" y="0" width="80" height="90" fill="url(#${id}h)" clip-path="url(#${id}f)"/>
<path d="M14 30C20 16 30 11 40 11" stroke="#CBFC11" stroke-width="3" fill="none" stroke-linecap="round"/>
<path d="M33 84L40 93L47 84Z" fill="#F7F6F1"/>
<rect x="33" y="98" width="14" height="40" rx="5" fill="#F7F6F1"/>
<path d="M33 104l14 5M33 112l14 5M33 120l14 5M33 128l14 5" stroke="#C9C6BA" stroke-width="1.5"/>
<rect x="31" y="136" width="18" height="6" rx="3" fill="#041650"/>
<path d="M40 142C34 148 36 150 40 150C44 150 46 148 40 142" stroke="#CBFC11" stroke-width="2.5" fill="none"/>
</svg>`;
};

/* --- Outils d'animation -------------------------------------------------- */
const layer = (css) => {
  const d = document.createElement('div');
  d.setAttribute('aria-hidden', 'true');
  d.style.cssText = 'position:fixed;left:0;top:0;pointer-events:none;z-index:2147483000;' + css;
  document.body.appendChild(d);
  return d;
};
const center = (el, cx = 0.5, cy = 0.5) => { const r = el.getBoundingClientRect(); return { x: r.left + r.width * cx, y: r.top + r.height * cy }; };
const q = (a, c, b, t) => { const u = 1 - t; return { x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y }; };
const cb = (a, c1, c2, b, t) => { const u = 1 - t; return { x: u ** 3 * a.x + 3 * u * u * t * c1.x + 3 * u * t * t * c2.x + t ** 3 * b.x, y: u ** 3 * a.y + 3 * u * u * t * c1.y + 3 * u * t * t * c2.y + t ** 3 * b.y }; };
const wait = (ms) => new Promise((r) => setTimeout(r, ms));
const clampX = (x) => Math.min(window.innerWidth - 24, Math.max(24, x));
const clampY = (y) => Math.min(window.innerHeight - 24, Math.max(24, y));

/** Fait suivre un chemin à la balle + une traînée de balles fantômes. Résout à l'arrivée de la balle. */
const runPath = (path, duration, lag = 0.03, count = 6) => new Promise((resolve) => {
  const SIZE = 32;
  const balls = Array.from({ length: count }, () => { const b = layer(`width:${SIZE}px;height:${SIZE}px;opacity:0;will-change:transform;filter:drop-shadow(0 5px 6px rgba(4,22,80,.3));`); b.innerHTML = ballSVG(); return b; });
  const start = performance.now();
  let arrived = false;
  const step = (now) => {
    const T = (now - start) / duration;
    balls.forEach((el, i) => {
      const t = T - i * lag;
      if (t < 0 || t > 1) { el.style.opacity = 0; return; }
      const p = path(t);
      const k = i === 0 ? 1 : 0.85 - i * 0.08;
      const sx = (p.sx || p.s || 1) * k, sy = (p.sy || p.s || 1) * k;
      el.style.opacity = i === 0 ? 1 : Math.max(0, 0.45 - i * 0.08);
      el.style.transform = `translate(${p.x - SIZE / 2}px,${p.y - SIZE / 2}px) rotate(${p.r || 0}deg) scale(${sx},${sy})`;
    });
    if (!arrived && T >= 1) { arrived = true; resolve(); }
    if (T < 1 + count * lag) requestAnimationFrame(step); else balls.forEach((b) => b.remove());
  };
  requestAnimationFrame(step);
});

/* --- L'animation --------------------------------------------------------- */
const animate = async (fromEl, target) => {
  const S = center(fromEl, 0.5, 0.15);
  const E = center(target);

  // 1. La raquette surgit à côté du bouton et frappe
  const rk = layer('width:62px;height:116px;transform-origin:31px 110px;opacity:0;');
  rk.innerHTML = racketSVG();
  const pos = `translate(${S.x - 92}px,${S.y - 100}px)`;
  rk.animate([
    { opacity: 0, transform: `${pos} rotate(-75deg) scale(.6)` },
    { opacity: 1, transform: `${pos} rotate(-75deg) scale(1)`, offset: 0.35 },
    { opacity: 1, transform: `${pos} rotate(30deg) scale(1.05)`, offset: 0.58 },
    { opacity: 0, transform: `${pos} rotate(42deg) scale(.9)` },
  ], { duration: 560, easing: 'cubic-bezier(.5,0,.3,1)', fill: 'forwards' }).finished.then(() => rk.remove());
  await wait(260);

  // 2. La balle part à l'opposé (contre-pied), revient en courbe taper la vitre, rebondit dans le panier
  const W = { x: window.innerWidth - 18, y: clampY(Math.min(window.innerHeight * 0.55, Math.max(E.y + 190, S.y - 80))) };
  const a1 = { x: clampX(S.x - 360), y: clampY(S.y - 380) };
  const a2 = { x: W.x - 240, y: clampY(W.y - 260) };
  const b1 = { x: W.x - 280, y: E.y - 40 };
  const duration = 1100;
  const split = 0.62;

  const glass = layer(`left:auto;right:0;top:${W.y - 170}px;width:16px;height:340px;background:linear-gradient(90deg,rgba(180,220,255,0),rgba(180,220,255,.9));border-left:2px solid rgba(255,255,255,.95);opacity:0;`);
  setTimeout(() => glass.animate([{ opacity: 0 }, { opacity: 1 }, { opacity: 0 }], { duration: 360 }).finished.then(() => glass.remove()), duration * split);

  await runPath((t) => {
    if (t < split) {
      const u = t / split;
      const k = u < 0.5 ? 2 * u * u : 1 - (-2 * u + 2) ** 2 / 2;
      return { ...cb(S, a1, a2, W, k), r: k * 720, s: 1.1 };
    }
    const k = (t - split) / (1 - split);
    if (k < 0.09) return { ...W, sx: 0.65, sy: 1.2, r: 720 };
    const e = 1 - (1 - k) ** 2;
    return { ...q(W, b1, E, e), r: 720 - k * 420, s: 1 - k * 0.45 };
  }, duration);

  // 3. Le panier encaisse + « +1 »
  target.animate(
    [{ transform: 'scale(1) rotate(0)' }, { transform: 'scale(1.35) rotate(-14deg)' }, { transform: 'scale(.9) rotate(8deg)' }, { transform: 'scale(1) rotate(0)' }],
    { duration: 400, easing: 'ease-out' }
  );
  const r = target.getBoundingClientRect();
  const plus = layer(`left:${r.left - 6}px;top:${r.bottom + 4}px;font-family:var(--cp-font-display,sans-serif);font-style:italic;font-weight:800;font-size:28px;line-height:1;color:#041650;background:#CBFC11;padding:3px 10px;border-radius:8px;opacity:0;`);
  plus.textContent = '+1';
  plus.animate([{ opacity: 0, transform: 'translateY(-8px) scale(.6)' }, { opacity: 1, transform: 'translateY(6px) scale(1.1)', offset: 0.3 }, { opacity: 0, transform: 'translateY(30px) scale(1)' }], { duration: 800, easing: 'ease-out' }).finished.then(() => plus.remove());
};

const findCartIcon = () => {
  for (const el of document.querySelectorAll('.cp-header__cart-icon, cart-icon')) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0 && r.bottom > 0) return el;
  }
  return null;
};

const flyBall = (fromEl) => {
  const target = findCartIcon();
  if (!fromEl || !target || reduceMotion() || !Element.prototype.animate) return Promise.resolve();
  return animate(fromEl, target).catch(() => {});
};

/* --- Panier ------------------------------------------------------------- */
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-cp-cart-drawer]') : null;
  if (!trigger) return;
  if (openDrawer()) event.preventDefault();
});

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.classList.contains('cp-product-form')) return;
  event.preventDefault();

  const launcher = event.submitter || form.querySelector('[type="submit"]');
  const buttons = [...form.querySelectorAll('[type="submit"]'), ...document.querySelectorAll(`[form="${form.id}"]`)];
  const status = form.querySelector('[data-cp-cart-status]');
  const setStatus = (html, success = false) => {
    if (!status) return;
    status.innerHTML = html;
    status.classList.toggle('is-success', success);
  };

  for (const b of buttons) b.setAttribute('aria-disabled', 'true');
  setStatus('Ajout en cours…');

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  try {
    const res = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await res.json();
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (!res.ok || data.status) throw new Error(data.description || data.message || 'Une erreur est survenue.');

    await flyBall(launcher);

    form.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    }));
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { didError: false, items: cart.items, source: 'cp-product-form', sourceId: form.id, itemCount: quantity, productId: form.dataset.productId, sections: data.sections },
    });

    updateLabels(cart.item_count);
    setStatus(`C'est dans le panier. <a href="${form.dataset.cartUrl}">Voir le panier</a>`, true);
    setTimeout(openDrawer, reduceMotion() ? 0 : 400);
  } catch (error) {
    if (status) {
      status.textContent = `Impossible d'ajouter au panier : ${error.message}`;
      status.classList.remove('is-success');
    }
  } finally {
    for (const b of buttons) b.removeAttribute('aria-disabled');
  }
});